import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import styled, { css } from 'styled-components';
// components
import BackSearchHeader from 'components/organisms/headers/BackSearch';
import ClassRowList from 'components/organisms/main/ClassRowList';
import ClassGridList from 'components/organisms/main/ClassGridList';
import ClassFilter from 'components/molecules/common/ClassFilter';
import SpinLoading from 'components/atoms/common/SpinLoading';
import MainLayout from 'cds/layout/Main';
import VerticalScrollLayout from 'cds/layout/VerticalScroll';
import Layout from 'components/layout';
// slices
import { setClassList } from 'slices/category';
import { getSubCategoryList, getCategoryClassList } from 'slices/category.thunk';
// hook
import useMetaData from 'hooks/common/useMetaData';
import { useSelector, useDispatch } from 'hooks/common/useStore';
import useClassFilter from 'hooks/domain/class/useFilter';
import useInfiniteScroll from 'hooks/common/useInfiniteScroll';
// styles
import { mediaQuery, mediaValue, palette, onlyHover } from 'cds/styles';
import * as typography from 'cds/typography';
import { setCategoryName, setClickableInformation } from 'slices/dataMining';

/**
 * 카테고리 페이지
 */
const CategoryPage = () => {
  const CALL_LIMIT = 20;

  const router = useRouter();
  const { categoryId, subCategoryId } = router.query;

  const dispatch = useDispatch();
  const { totalCount } = useSelector(state => state.category.info);
  const subCategoryList = useSelector(state => state.category.subCategoryList);
  const classList = useSelector(state => state.category.classList);
  const categoryList = useSelector(state => state.category.categoryList);

  const [category, setCategory] = useState({});
  const [subCategory, setSubCategory] = useState([]);
  const [isCategoryFold, setIsCategoryFold] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const page = useRef(0);
  const enableMoreData = useRef(true);
  const target = useRef(null);

  const { currentFilter, FilterDropdown, FilterSheet, onFilterShow } = useClassFilter([
    0, 1, 3, 4, 5,
  ]);
  const { MetaTitle } = useMetaData();

  useEffect(() => {
    const categoryName = subCategoryId ? `${categoryId} / ${subCategoryId}` : `${categoryId}`;
    dispatch(setClickableInformation({ [categoryName]: classList }));
    dispatch(setCategoryName(categoryName));
  }, [classList, categoryId, subCategoryId, dispatch]);

  useInfiniteScroll({
    target,
    onIntersect: ([entry]) => {
      if (entry.isIntersecting) {
        onLoadData();
      }
    },
  });

  const onLoadData = useCallback(async () => {
    if (!enableMoreData.current) {
      return;
    }

    if (!categoryId) {
      return;
    }

    setIsLoading(true);
    enableMoreData.current = false;

    const data = {
      category: categoryId,
      sort: currentFilter.sort,
      order: currentFilter.order,
      skip: page.current * CALL_LIMIT,
      limit: CALL_LIMIT,
    };

    if (subCategoryId) {
      Object.assign(data, { subCategory: subCategoryId });
    }

    const res = await dispatch(getCategoryClassList(data));

    setIsLoading(false);
    if (res.payload && res.payload.length) {
      page.current += 1;
      enableMoreData.current = true;
    }
  }, [categoryId, currentFilter.order, currentFilter.sort, dispatch, subCategoryId]);

  /* 카테고리 전체 클릭 */
  const onClickAll = () => {
    router.push({ query: { categoryId } });
  };

  /** 카테고리 버튼 클릭 */
  const onClickSub = (cur, now) => {
    if (cur !== now) {
      dispatch(setClassList([]));
      router.push({ query: { categoryId, subCategoryId: now } });
    }
  };

  /** 하위 카테고리 데이터 호출 */
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (!categoryId) {
      router.replace('/');
      return;
    }

    const hasOwnSubCategory = Object.prototype.hasOwnProperty.call(subCategoryList, categoryId);

    if (hasOwnSubCategory && subCategoryList[categoryId]) {
      if (subCategoryId) {
        const hasSubCategory = subCategoryList[categoryId].interests.find(v => v === subCategoryId);

        // 없는 카테고리로 접근시에 상위 카테고리로 이동한다.
        if (!hasSubCategory) {
          return router.push({ query: { categoryId } });
        }
      }

      return setSubCategory(subCategoryList[categoryId].interests);
    }

    // 없는 카테고리로 접근시에 탐색 화면으로 이동한다.
    if (hasOwnSubCategory && !subCategoryList[categoryId]) {
      return router.push(`/search?keyword=${categoryId}`);
    }

    dispatch(getSubCategoryList({ category: categoryId }));
  }, [dispatch, router.isReady, categoryId, subCategoryList, router, subCategoryId]);

  useEffect(() => {
    page.current = 0;
    enableMoreData.current = true;
    onLoadData();

    return () => {
      dispatch(setClassList([]));
    };
  }, [dispatch, onLoadData, categoryId, subCategoryId]);

  useEffect(() => {
    if (categoryList.length === 0) {
      return;
    }

    const [filtered] = categoryList.filter(v => v.category === categoryId);
    setCategory(filtered ? filtered : {});
  }, [categoryId, categoryList]);

  const CategoryButtonList = subCategory.map(category => (
    <li key={category}>
      <ChipButton
        selected={subCategoryId === category}
        onClick={() => onClickSub(subCategoryId, category)}
      >
        {category}
      </ChipButton>
    </li>
  ));

  return (
    <>
      <MetaTitle content={`${categoryId ? categoryId : ''}`} />

      <Wrapper>
        <ContentLayout>
          <CategoryBanner
            src={category.banner}
            color={category.fontcolor}
            backColor={category.backcolor}
          >
            <h1>{categoryId}</h1>
          </CategoryBanner>
          {categoryId && (
            <>
              <TitleSection>하위 카테고리</TitleSection>
              <CategoryList>
                <ul>
                  <ChipButton selected={!subCategoryId} onClick={onClickAll}>
                    전체
                  </ChipButton>
                  {isCategoryFold ? CategoryButtonList.slice(0, 6) : CategoryButtonList}
                  {CategoryButtonList.length > 6 && (
                    <ChipButton onClick={() => setIsCategoryFold(v => !v)}>
                      {isCategoryFold ? '더보기' : '접기'}
                    </ChipButton>
                  )}
                </ul>
                <ul>
                  <ChipButton selected={!subCategoryId} onClick={onClickAll}>
                    전체
                  </ChipButton>
                  {CategoryButtonList}
                </ul>
              </CategoryList>
            </>
          )}
          <Divide />
          <ClassFilter
            classTotalCount={totalCount}
            sortName={currentFilter.name}
            onClickSort={onFilterShow}
          >
            <FilterDropdown />
          </ClassFilter>
          <ClassWrapper>
            <ClassRowList list={classList} showPrice />
            <ClassGridList
              list={classList}
              showSalePer
              showPrice
              slidePerView={[
                {
                  break: 'default',
                  view: 1,
                },
                {
                  break: mediaValue.medium,
                  view: 2,
                },
                {
                  break: mediaValue.xlarge,
                  view: 3,
                },
                {
                  break: 1376,
                  view: 4,
                },
                {
                  break: 1900,
                  view: 5,
                },
              ]}
            />
          </ClassWrapper>
          <div ref={target} />
          {isLoading && (
            <LoadingLayout>
              <SpinLoading />
            </LoadingLayout>
          )}
        </ContentLayout>
      </Wrapper>

      <FilterSheet />
    </>
  );
};

const CategoryHeader = () => {
  const router = useRouter();
  const { categoryId } = router.query;

  return <BackSearchHeader title={`${categoryId ? categoryId : ''}`} />;
};

CategoryPage.getLayout = function getLayout(page) {
  return <Layout mobileHeader={<CategoryHeader />}>{page}</Layout>;
};

const sectionPadding = css`
  padding: 0 16px;

  ${mediaQuery.large} {
    padding: 0;
  }
`;

const Wrapper = styled(MainLayout)`
  width: 1800px;

  ${mediaQuery.large} {
    padding: 0 72px;
  }
`;

const ContentLayout = styled(VerticalScrollLayout)`
  ${mediaQuery.large} {
    padding-bottom: 128px;
  }
`;

const CategoryBanner = styled.div`
  overflow: hidden;
  position: relative;
  padding-bottom: 24%;
  height: 0px;
  background: ${({ src, backColor }) => {
    if (src) {
      return `url(${src}), ${backColor ? backColor : palette.grey010}`;
    } else {
      return backColor ? backColor : palette.grey010;
    }
  }};
  background-position: 75% center;
  background-repeat: no-repeat;
  background-size: cover;

  ${mediaQuery.medium} {
    padding-bottom: 16%;
  }

  ${mediaQuery.large} {
    padding-bottom: 10%;
    margin: 24px auto;
    border-radius: 8px;
  }

  & > h1 {
    position: absolute;
    display: flex;
    align-items: center;
    height: 100%;
    margin-left: 28px;
    ${typography.subtitle2_bold}
    font-size: 24px;
    color: ${({ color }) => color};
    z-index: 1;

    ${mediaQuery.medium} {
      margin-left: 56px;
      font-size: 24px;
    }
  }

  ${mediaQuery.large} {
    display: block;
    padding-bottom: 15%;
  }

  ${mediaQuery.xlarge} {
    padding-bottom: 10%;

    & > h1 {
      font-size: 28px;
    }
  }
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  ${typography.subtitle3_medium};
  ${sectionPadding};
  color: ${palette.grey75};
  ${mediaQuery.medium} {
    height: 68px;
  }
`;

const CategoryList = styled.section`
  padding-bottom: 10px;
  margin-bottom: 8px;
  ${sectionPadding};

  & > ul {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;

    & > li {
      margin: 0 8px 8px 0;
    }

    & > button {
      margin: 0 8px 8px 0;
    }

    & > button:last-child {
      background-color: ${palette.BTN.BG};
    }
  }

  & > ul:first-child {
    display: flex;
  }

  & > ul:last-child {
    display: none;
  }

  ${mediaQuery.large} {
    & > ul:first-child {
      display: none;
    }

    & > ul:last-child {
      display: flex;
    }
  }
`;

const ChipButton = styled.button`
  display: flex;
  align-items: center;
  position: relative;
  height: 32px;
  padding: 0 12px;
  border: 1px solid ${palette.system.grey};
  border-radius: 32px;
  background-color: ${({ selected }) => (selected ? palette.grey080 : 'transparent')};
  color: ${({ selected }) => (selected ? palette.grey75 : palette.grey50)};
  ${typography.body2_regular};
  white-space: nowrap;
  transition: 0.1s ease;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 32px;
  }

  ${onlyHover} {
    &:hover {
      ${({ disabled }) =>
        !disabled &&
        css`
          &::before {
            background-color: rgba(0, 0, 0, 0.04);
          }
        `};
    }
  }

  &:active {
    ${({ disabled }) =>
      !disabled &&
      css`
        &::before {
          background-color: rgba(0, 0, 0, 0.04);
        }
      `};
  }

  ${mediaQuery.large} {
    font-size: 14px;
  }
`;

const ClassWrapper = styled.div`
  position: relative;
  z-index: 0;

  & > ul:first-child {
    display: block;
    padding: 0 16px;
  }

  & > ul:last-child {
    display: none;
  }

  ${mediaQuery.large} {
    margin-top: 32px;

    & > ul:first-child {
      display: none;
    }

    & > ul:last-child {
      display: block;
    }
  }
`;

const LoadingLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 128px;
`;

const Divide = styled.hr`
  height: 10px;
  margin: 0;
  border-style: solid;
  border-width: 1px 0 0 0;
  border-color: ${palette.system.BNB};
  border: none;
  background-color: ${palette.system.Null};

  ${mediaQuery.large} {
    height: 1px;
    margin: 16px 0 32px;
    border-top: 1px solid ${palette.system.grey};
    background-color: ${palette.system.grey};
    border-color: ${palette.system.grey};
  }
`;

export default CategoryPage;
